import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, Sparkles, ListChecks } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, Spinner, Chip, Empty } from "../components/ui";

const CAT_TONE = { business: "blue", dispute: "amber", general: "purple", personal: "green" };

export default function Tasks() {
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");

  const load = () => api.get("/tasks").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { await api.post("/tasks", { title, category: "general" }); setTitle(""); load(); }
    catch { toast.error("Could not add task"); }
  };
  const toggle = async (id) => { await api.patch(`/tasks/${id}`); load(); };
  const remove = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  if (data === null) return <Spinner />;

  const custom = data.tasks.filter((t) => !t.generated && t.title);
  const generated = data.generated || [];

  return (
    <div className="space-y-6">
      <PageHeader label="Stay on track" title="Task Manager"
        subtitle="AI-generated next steps plus your own tasks — payments, renewals, dispute follow-ups, and goals." />

      <GlassCard className="p-4">
        <form onSubmit={add} className="flex gap-2.5">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a task…"
            className="cc-input flex-1 rounded-xl px-4 py-2.5 text-sm" data-testid="task-input" />
          <button type="submit" className="cc-btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-500" data-testid="task-add">
            <Plus size={16} /> Add
          </button>
        </form>
      </GlassCard>

      <div className="grid lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-4 flex items-center gap-2">
            <Sparkles size={17} className="text-purple-300" /> AI-Generated Tasks
          </h3>
          <div className="space-y-2">
            {generated.length === 0 && <p className="text-sm text-slate-500">No suggested tasks — you're on top of it! 🎯</p>}
            {generated.map((t) => (
              <div key={t.id} className="flex items-center gap-3 rounded-lg cc-surface border border-white/[0.05] px-4 py-3" data-testid="generated-task">
                <button onClick={() => toggle(t.id)} className="h-5 w-5 shrink-0 rounded-md border border-white/20 flex items-center justify-center hover:border-blue-400">
                  <Check size={12} className="text-transparent" />
                </button>
                <span className="text-sm text-slate-200 flex-1">{t.title}</span>
                <Chip tone={CAT_TONE[t.category] || "default"}>{t.category}</Chip>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="cc-heading text-lg font-500 text-white mb-4 flex items-center gap-2">
            <ListChecks size={17} className="text-blue-300" /> Your Tasks
          </h3>
          {custom.length === 0 ? (
            <Empty icon={ListChecks} title="No tasks yet" subtitle="Add your own tasks to stay organized." />
          ) : (
            <div className="space-y-2">
              {custom.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-lg cc-surface border border-white/[0.05] px-4 py-3" data-testid={`task-${t.id}`}>
                  <button onClick={() => toggle(t.id)} className={`h-5 w-5 shrink-0 rounded-md border flex items-center justify-center ${t.done ? "cc-gradient border-transparent" : "border-white/20"}`}>
                    {t.done && <Check size={12} className="text-white" />}
                  </button>
                  <span className={`text-sm flex-1 ${t.done ? "text-slate-500 line-through" : "text-slate-200"}`}>{t.title}</span>
                  <button onClick={() => remove(t.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
